import { useState } from 'react';
import { STAGE_LABEL, matchesPerson, orderAssignees, type Stage } from '@/domain';
import { useKitAssignees } from '@/application/usecases/useKits';
import { useAssigneeOrder } from '@/application/usecases/useAssigneeOrder';
import { strings } from '@/shared/strings';
import { AssigneeLine, LeastBusyToggle } from '@/ui/data/AssigneeLine';
import { CheckIcon, SearchIcon } from '@/ui/icons';
import { ConfirmSheet } from '@/ui/primitives/ConfirmSheet';
import styles from './StagePickerDialog.module.css';

export interface StagePickerDialogProps {
  title: string;
  lead: string;
  confirmLabel: string;
  destinations: Stage[];
  /** A flag requires a reason. Section 7.4, and section 5 of the request of 4 October 2026. */
  requireNote: boolean;
  /** Mark done takes a note for Lab Notes, never required. Request of 4 October 2026, section 5. */
  optionalNote?: boolean;
  /**
   * What confirming will do, in plain words, for the department and person
   * chosen so far: "Mark JO-2047 as done and send it to Final Check?".
   */
  statement: (stage: Stage | null, person: string | null) => string;
  onCancel: () => void;
  onConfirm: (toStage: Stage, note: string, assignToUserId: string | null) => void;
  busy?: boolean;
  /**
   * Selected when the sheet opens. The usual next station is preselected, so
   * the common case is one confirming tap.
   */
  initial?: Stage | null;
  /**
   * The case, for looking up who works the chosen bench. Section 5 of the request
   * of 20 September 2026: once a department is chosen, the people who work it are
   * listed and the case can be handed to one of them.
   */
  kitId?: string;
  /**
   * The destination is decided and only the person is chosen. In automatic
   * routing a case on its route goes to the next bench whatever is picked here.
   */
  peopleOnly?: boolean;
}

/**
 * "Where does the case go next?": the sheet Flag and Mark done both open.
 * Request of 4 October 2026, section 6.
 *
 * The departments as a list, the usual next one already chosen; then, once a
 * department is chosen, the people who work it, searchable by name, each with
 * the department they belong to; then the note, a reason a flag cannot go
 * without or a line for Lab Notes on Mark done; and one button that says where
 * the case is going. It slides up from the bottom of a phone and is pulled
 * down to close.
 *
 * Send for rework opens it too, and chooses a technician the way an assignment
 * does: each person with their department and how many cases they hold now,
 * least busy first on request. Sections 7 and 8 of the request of
 * 5 October 2026.
 */
export function StagePickerDialog({
  title,
  lead,
  confirmLabel,
  destinations,
  requireNote,
  optionalNote = false,
  onCancel,
  onConfirm,
  busy = false,
  initial = null,
  kitId,
  peopleOnly = false,
  statement,
}: StagePickerDialogProps) {
  const [stage, setStage] = useState<Stage | null>(initial);
  const [note, setNote] = useState('');
  const [assignTo, setAssignTo] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  /*
   * Who works the chosen bench. Asked of the SERVER against the destination,
   * because who may work a bench is its rule -- management any bench, Intake
   * whoever may create a case -- and a filtered copy on this side would drift
   * from what the completion refuses. Section 5.3 of the request of
   * 20 September 2026.
   */
  const people = useKitAssignees(kitId, stage ?? undefined);
  const candidates = people.data ?? [];
  const { order, leastBusy, setLeastBusy } = useAssigneeOrder();
  /*
   * The department chosen lists its own people; searching finds anybody, since a
   * case may be given to anybody. Request of 5 October 2026. A bench with nobody
   * of that department (Final Check is worked by permission) lists who may work it.
   */
  const ofDepartment = candidates.filter((person) => person.department === stage);
  const listed = ofDepartment.length > 0 ? ofDepartment : candidates;
  const matches = orderAssignees(
    search.trim() === '' ? listed : candidates.filter((person) => matchesPerson(search, person)),
    order,
  );
  const chosen = candidates.find((person) => person.id === assignTo) ?? null;

  function confirm(): boolean {
    if (stage === null) {
      setError(strings.flag.pickDepartment);
      return false;
    }
    if (requireNote && note.trim() === '') {
      setError(strings.flag.reasonRequired);
      return false;
    }
    onConfirm(stage, note.trim(), assignTo);
    return true;
  }

  const showNote = requireNote || optionalNote;

  return (
    <ConfirmSheet
      title={title}
      statement={statement(stage, chosen?.name ?? null)}
      confirmLabel={confirmLabel}
      cancelLabel={strings.flag.cancel}
      closeLabel={strings.sheet.close}
      onConfirm={confirm}
      onCancel={onCancel}
      busy={busy}
      unsaved={
        note.trim() === ''
          ? null
          : { question: strings.sheet.unsaved, keep: strings.sheet.keep, discard: strings.sheet.discard }
      }
    >
      <p className={styles.lead}>{lead}</p>
      <div className={styles.section}>
        <span className={styles.label} id="where-label">
          {strings.flag.sendTo}
        </span>
        <div className={styles.departments} role="group" aria-labelledby="where-label">
          {(peopleOnly && stage !== null ? [stage] : destinations).map((destination) => (
            <button
              key={destination}
              type="button"
              aria-pressed={stage === destination}
              aria-disabled={peopleOnly || undefined}
              className={`${styles.department} ${stage === destination ? styles.on : ''}`}
              onClick={() => {
                if (peopleOnly) return;
                setStage(destination);
                // The people list is about the bench, so a new bench means a
                // new list and whoever was chosen from the old one is gone.
                setAssignTo(null);
                setSearch('');
                setError(null);
              }}
            >
              <span>{STAGE_LABEL[destination]}</span>
              {destination === initial && (
                <span className={styles.hint} aria-hidden="true">
                  {strings.routeChoice.usual}
                </span>
              )}
              {stage === destination && <CheckIcon size={18} className={styles.tick} />}
            </button>
          ))}
        </div>
      </div>

      {/*
        And then the person, once a department is chosen. "Nobody in particular"
        comes first and is chosen, because that is what most hand-offs are: the
        case lands on the bench and whoever picks it up scans it.
      */}
      {stage !== null && candidates.length > 0 && (
        <div className={styles.section}>
          <span className={styles.label} id="who-label">
            {strings.flag.sendToPerson}
          </span>
          {candidates.length > 3 && (
            <label className={styles.search}>
              <SearchIcon size={15} />
              <input
                type="search"
                aria-label={strings.routeChoice.searchPeople}
                placeholder={strings.routeChoice.searchPeople}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>
          )}
          {candidates.length > 1 && <LeastBusyToggle on={leastBusy} onChange={setLeastBusy} />}
          <div className={styles.people} role="group" aria-labelledby="who-label">
            <button
              type="button"
              aria-pressed={assignTo === null}
              className={`${styles.person} ${assignTo === null ? styles.on : ''}`}
              onClick={() => setAssignTo(null)}
            >
              <span className={styles.name}>{strings.flag.nobodyInParticular}</span>
              {assignTo === null && <CheckIcon size={18} className={styles.tick} />}
            </button>
            {matches.map((person) => (
              <button
                key={person.id}
                type="button"
                aria-pressed={assignTo === person.id}
                className={`${styles.person} ${assignTo === person.id ? styles.on : ''}`}
                onClick={() => setAssignTo(person.id)}
              >
                <AssigneeLine
                  name={person.name}
                  department={person.department}
                  activeCases={person.activeCases}
                />
                {assignTo === person.id && <CheckIcon size={18} className={styles.tick} />}
              </button>
            ))}
            {matches.length === 0 && <p className={styles.none}>{strings.routeChoice.noMatch}</p>}
          </div>
        </div>
      )}

      {showNote && (
        <div className={styles.section}>
          <label className={styles.label} htmlFor="flag-note">
            {requireNote ? strings.flag.reason : strings.routeChoice.note}
          </label>
          <textarea
            id="flag-note"
            className={styles.note}
            rows={2}
            value={note}
            placeholder={requireNote ? strings.flag.reasonPlaceholder : strings.routeChoice.notePlaceholder}
            onChange={(event) => {
              setNote(event.target.value);
              setError(null);
            }}
          />
        </div>
      )}

      {error !== null && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
    </ConfirmSheet>
  );
}
